import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import {resolveTab} from "src/utils/tab.util";
import {Box, Button, Divider, Stack, Tab} from "@mui/material";
import {TabContext} from "@mui/lab";
import PrintIcon from "@mui/icons-material/Print";
import * as React from "react";
import {useTranslation} from "react-i18next";
import {useEntity} from "crqlar-react-common/dist/components/useEntity";
import {ReservationService} from "crqlar-react-common/dist/services/reservation/reservation.service";
import FolioList from "../billing/folio/FolioList";
import ReservationDetailsForm from "./ReservationDetailsForm";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import LogDetails from "crqlar-react-common/dist/components/logs/LogDetails";
import SettingsEntityHeader from "../layout/SettingsEntityHeader";
import Typography from "@mui/material/Typography";
import DangerZone from "crqlar-react-common/dist/components/DangerZone";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import ScrollableTabList from "crqlar-react-common/dist/components/tabs/ScrollableTabList";
import DigitalSigningDetailsDialog from "../../digitalSigning/details/DigitalSigningDetailsDialog";
import {isSignedDigitalSigning} from "../../digitalSigning/share/digitalSigningFlow.util";
import DigitalSigningSignatureButton from "../../digitalSigning/share/DigitalSigningSignatureButton";
import {useLatestReservationDigitalSigning} from "../../digitalSigning/share/useLatestDigitalSigning";

const ReservationDetails = () => {

    //region State

    useTrackScreenVisit('system reservation details')

    const {t} = useTranslation()
    const {id} = useParams()
    const navigate = useNavigate()
    const readOnly = useReadOnly()

    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab = resolveTab(searchParams.get('tab'), ['basic', 'folios', 'userLogs'], 'basic');
    const setActiveTab = (newTab: string) => {
        const nextSearchParams = new URLSearchParams(searchParams);
        nextSearchParams.set('tab', newTab);
        setSearchParams(nextSearchParams);
    };

    const reservationData = useEntity({
        get: async () => {
            if (id && id !== 'new') {
                return ReservationService.getReservationById(id)
            } else {
                return {}
            }
        },
        create: ReservationService.createReservation,
        update: ReservationService.updateReservation,
        delete: (id) => ReservationService.deleteReservation({id: [id]}),
        onDeleted: () => {
            navigate('/system/reservations', {replace: true})
        },
        onSaved: (reservation, wasCreateAction, prev) => {
            if (wasCreateAction) {
                navigate(`/system/reservations/${reservation?.id}`, {replace: true})
            }
        },
    }, id)


    const reservation = reservationData.item

    // Its latest signing, once the guest signed it: the header shows what they signed -
    // whatever the settings offer now, like the folio view's chip
    const latestDigitalSigning = useLatestReservationDigitalSigning(reservation.id).data
    const signedDigitalSigning = isSignedDigitalSigning(latestDigitalSigning?.status) ? latestDigitalSigning : undefined
    const [showDigitalSigning, setShowDigitalSigning] = React.useState(false)

    //endregion State

    //region UI

    const headerSection = (
        <SettingsEntityHeader
            title={reservation.id ? `#${reservation.id}` : undefined}
            data={reservationData}
            back={() => navigate('/system/reservations')}
            extraActions={reservation.id && (
                <>
                    {!!signedDigitalSigning && (
                        <DigitalSigningSignatureButton
                            signing={signedDigitalSigning}
                            size={'small'}
                            onClick={() => setShowDigitalSigning(true)}
                        />
                    )}
                    <Button
                        variant={'text'}
                        startIcon={<PrintIcon/>}
                        onClick={async () => {
                            // Save first so the printout reflects unsaved edits (mirrors the folio
                            // details "Print" action). folioItemId is optional on the legacy route —
                            // omitting it prints the confirmation without folio charges.
                            if (reservationData.isDirty) {
                                await reservationData.save()
                            }
                            window.open(`/print/reservationConfirmation/${reservation.id}`, '_blank')
                        }}
                    >
                        {t('Print')}
                    </Button>
                </>
            )}
        />
    )

    const dangerZoneSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <DangerZone
                title={t('Delete reservation')}
                description={t('This permanently deletes the reservation. This action cannot be undone.')}
                buttonLabel={t('Delete')}
                loading={reservationData.saving}
                disabled={readOnly}
                onDelete={() => reservationData.removeNow()}
            />
            <Typography variant={'body2'} color={'text.secondary'} textAlign={'start'}>
                {t('Last updated')}: {reservation.updateDate?.toLocaleString() || '-'}
            </Typography>
        </Stack>
    )

    const reservationDataUI = (
        <Stack
            sx={{
                width: '100%',
                maxWidth: '900px',
                mx: 'auto',
                mt: 3,
                gap: 2,
            }}
        >
            {
                reservation.id &&
                <Box sx={{mb: 4}}>
                    <TabContext value={activeTab}>
                        <ScrollableTabList onChange={(e, v) => setActiveTab(v)}>
                            <Tab label={t('Basic info')} value={'basic'}/>
                            <Tab label={t('Folios')} value={'folios'}/>
                            <Tab label={t('User logs')} value={'userLogs'}/>
                        </ScrollableTabList>
                    </TabContext>
                </Box>

            }

            {
                activeTab === 'basic' &&
                <>
                    <ReservationDetailsForm
                        reservationData={reservationData}
                    />

                    {
                        reservation.id &&
                        <>
                            <Divider sx={{mt: 4}}/>
                            {dangerZoneSection}
                        </>
                    }
                </>
            }

            {
                activeTab === 'folios' &&
                <div>
                    <FolioList reservationId={id}/>
                </div>
            }

            {
                activeTab === 'userLogs' &&
                <div>
                    <LogDetails entityId={id}/>
                </div>
            }
        </Stack>
    )

    return (
        <Box sx={{width: '100%', pb: 8}}>
            {headerSection}
            {reservationDataUI}

            {showDigitalSigning && !!signedDigitalSigning && (
                <DigitalSigningDetailsDialog
                    id={signedDigitalSigning.id}
                    open
                    onClose={() => setShowDigitalSigning(false)}
                />
            )}
        </Box>
    );
}

export default ReservationDetails;
